import React, { useCallback, useState } from 'react';
import { Grid, Typography, Box, Tooltip, IconButton } from '@mui/material';
import {
  People,
  LinkedIn,
  Paid,
  CardMembership,
  TrendingUp,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { FcGoogle } from 'react-icons/fc';
import { MetricCard } from './styled/MetricCard';

// Sensitive metric values are replaced with this mask in the DOM when
// hidden — nothing about the real value leaks via view-source or devtools.
// Fixed length so observers can't infer the magnitude from the mask width.
const SENSITIVE_MASK = '••••••';
const SENSITIVE_SUBLINE_MASK = '•••••';
const SESSION_KEY_PREFIX = 'admin-metrics-reveal:';

// Reveals persist within a single browser tab (sessionStorage) so a hard
// refresh doesn't re-hide everything the admin already chose to see. A new
// tab / browser restart clears sessionStorage, returning everything to the
// default hidden state.
const buildInitialRevealed = () => {
  const revealed = new Set();
  if (typeof window === 'undefined') return revealed;
  try {
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (
        key &&
        key.startsWith(SESSION_KEY_PREFIX) &&
        window.sessionStorage.getItem(key) === '1'
      ) {
        revealed.add(key.slice(SESSION_KEY_PREFIX.length));
      }
    }
  } catch (_e) {
    // sessionStorage may be unavailable (privacy mode, etc.) — fall back to
    // an empty set so everything defaults to hidden.
  }
  return revealed;
};

const MetricsCards = ({ metrics }) => {
  const [revealedTitles, setRevealedTitles] = useState(buildInitialRevealed);

  const toggleReveal = useCallback((title) => {
    setRevealedTitles((prev) => {
      const next = new Set(prev);
      const storageKey = `${SESSION_KEY_PREFIX}${title}`;
      if (next.has(title)) {
        next.delete(title);
        try {
          window.sessionStorage.removeItem(storageKey);
        } catch (_e) {
          // ignore
        }
      } else {
        next.add(title);
        try {
          window.sessionStorage.setItem(storageKey, '1');
        } catch (_e) {
          // ignore
        }
      }
      return next;
    });
  }, []);

  const isRevealed = useCallback(
    (title) => revealedTitles.has(title),
    [revealedTitles]
  );

  const linkedinUsers = Number(metrics.linkedinUsers) || 0; // A
  const googleUsers = Number(metrics.googleUsers) || 0; // B (inclusive)
  const bothProvidersUsers = Number(metrics.bothProvidersUsers) || 0; // A ∩ B
  const totalAuthUsers = Math.max(
    0,
    linkedinUsers + googleUsers - bothProvidersUsers
  ); // A ∪ B
  const googleOnlyUsers = Math.max(0, googleUsers - bothProvidersUsers); // B - A (set difference)

  const formatRevenue = (value) => {
    const amount = Number(value) || 0;
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getValueFontSize = (value) => {
    const valueLength = String(value ?? '').length;
    if (valueLength >= 18) return { xs: '0.95rem', sm: '1.1rem', md: '1.2rem' };
    if (valueLength >= 14) return { xs: '1.05rem', sm: '1.2rem', md: '1.35rem' };
    if (valueLength >= 10) return { xs: '1.2rem', sm: '1.35rem', md: '1.55rem' };
    return { xs: '1.45rem', sm: '1.7rem', md: '1.9rem' };
  };

  const getMonthlyTrend = () => {
    const percent = Number(metrics.monthlyRevenueChangePercent) || 0;
    const trend = (metrics.monthlyRevenueTrend || '').toUpperCase();
    if (trend === 'UP') {
      return {
        label: `+${percent.toFixed(2)}% MoM`,
        color: '#66bb6a',
      };
    }
    if (trend === 'DOWN') {
      return {
        label: `${percent.toFixed(2)}% MoM`,
        color: '#ef5350',
      };
    }
    return {
      label: `${percent.toFixed(2)}% MoM`,
      color: '#cbd5e1',
    };
  };

  const monthlyTrend = getMonthlyTrend();

  const metricsConfig = [
    {
      title: 'Registered Today',
      value: metrics.usersRegisteredToday || 0,
      icon: <People sx={{ fontSize: 30 }} />,
      caption: ' ',
    },
    {
      title: 'Total Users',
      value: totalAuthUsers,
      icon: <People sx={{ fontSize: 30 }} />,
      caption: 'A ∪ B',
    },
    {
      title: 'Admins',
      value: metrics.adminCount || 0,
      icon: <People sx={{ fontSize: 30 }} />,
      caption: ' ',
    },
    {
      title: 'LinkedIn Users',
      value: linkedinUsers,
      icon: <LinkedIn sx={{ fontSize: 30, color: '#0A66C2' }} />,
      caption: 'A',
    },
    {
      title: 'Google Only Users',
      value: googleOnlyUsers,
      icon: <FcGoogle size={30} />,
      caption: 'B - A',
    },
    {
      title: 'Live Users',
      value: metrics.liveUsers || 0,
      icon: <People sx={{ fontSize: 30 }} />,
      caption: 'Distinct Auth Users',
      tooltip:
        'Distinct user emails with authenticated, not-expired active sessions, excluding configured excluded-user-emails. Not a guarantee user is online.',
    },
    // {
    //   title: 'Both Providers',
    //   value: bothProvidersUsers,
    //   icon: (
    //     <Box
    //       sx={{
    //         display: 'flex',
    //         gap: 0.5,
    //         alignItems: 'center',
    //         height: '30px', // Match single icon height
    //         justifyContent: 'center',
    //       }}
    //     >
    //       <Google sx={{ fontSize: 24 }} />
    //       <LinkedIn sx={{ fontSize: 24 }} />
    //     </Box>
    //   ),
    //   caption: 'A ∩ B',
    // },
    {
      title: 'Active Subscriptions',
      value: metrics.activeSubscriptions || 0,
      icon: <CardMembership sx={{ fontSize: 30 }} />,
      caption: 'Currently Active',
      tooltip:
        'Subscriptions currently active right now (excluding cancelled and expired).',
      sensitive: true,
    },
    {
      title: 'Purchased Subscriptions',
      value: metrics.purchasedSubscriptions || 0,
      icon: <CardMembership sx={{ fontSize: 30 }} />,
      caption: 'All-Time Purchases',
      tooltip:
        'Total subscriptions purchased over time, including active, scheduled, expired, and cancelled.',
      sensitive: true,
    },
    {
      title: 'Monthly Revenue',
      value: formatRevenue(metrics.monthlyRevenue),
      icon: <TrendingUp sx={{ fontSize: 30 }} />,
      caption: 'Current Month',
      subline: monthlyTrend.label,
      sublineColor: monthlyTrend.color,
      tooltip:
        'Revenue from completed payments in the current month compared to previous month.',
      sensitive: true,
    },
    {
      title: 'All-Time Revenue',
      value: formatRevenue(metrics.totalRevenue),
      icon: <Paid sx={{ fontSize: 30 }} />,
      caption: `${metrics.totalCompletedPayments || 0} Payments`,
      tooltip:
        'Total completed payment value collected across all time.',
      sensitive: true,
    },
    // {
    //   title: 'Sessions',
    //   value: `${metrics.activeSessions ?? 0}/${metrics.totalSessions ?? 0}`,
    //   icon: <Devices sx={{ fontSize: 30 }} />,
    //   caption: 'Active/Total (Auth)',
    //   tooltip:
    //     'Authenticated sessions that are not expired. Active = status active; Total = active + inactive.',
    // },
  ]

  return (
    <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: 4 }}>
      {metricsConfig.map((metric, index) => {
        const hidden = metric.sensitive && !isRevealed(metric.title);
        const displayedValue = hidden ? SENSITIVE_MASK : metric.value;
        const displayedSubline = hidden
          ? metric.subline
            ? SENSITIVE_SUBLINE_MASK
            : metric.subline
          : metric.subline;
        const sublineColor = hidden ? '#cbd5e1' : metric.sublineColor;

        return (
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={index}>
            <MetricCard>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  mb: metric.caption ? 1 : 1.35,
                  minHeight: '28px',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    color: '#ffcf51',
                    display: 'flex',
                    alignItems: 'center',
                    height: '28px',
                  }}
                >
                  {metric.icon}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    maxWidth: '65%',
                  }}
                >
                  {metric.caption && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#d1d5db',
                        fontSize: { xs: '0.66rem', sm: '0.72rem' },
                        lineHeight: 1.2,
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {metric.caption}
                    </Typography>
                  )}
                  {metric.sensitive && (
                    <Tooltip
                      title={
                        isRevealed(metric.title)
                          ? `Hide ${metric.title}`
                          : `Show ${metric.title}`
                      }
                      arrow
                    >
                      <IconButton
                        onClick={() => toggleReveal(metric.title)}
                        size="small"
                        aria-label={
                          isRevealed(metric.title)
                            ? `Hide ${metric.title}`
                            : `Show ${metric.title}`
                        }
                        aria-pressed={isRevealed(metric.title)}
                        sx={{
                          p: 0.25,
                          color: '#d1d5db',
                          '&:hover': {
                            color: '#ffcf51',
                            backgroundColor: 'rgba(255, 207, 81, 0.08)',
                          },
                        }}
                      >
                        {isRevealed(metric.title) ? (
                          <VisibilityOff sx={{ fontSize: '1rem' }} />
                        ) : (
                          <Visibility sx={{ fontSize: '1rem' }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 0.45,
                  color: '#ffcf51',
                  fontSize: getValueFontSize(displayedValue),
                  lineHeight: 1.14,
                  maxWidth: '100%',
                  whiteSpace: 'nowrap',
                  overflowWrap: 'normal',
                  wordBreak: 'normal',
                  // CSS blur on top of the masked text — observer sees a fuzzy
                  // blob, no real value is in the DOM either way.
                  filter: hidden ? 'blur(6px)' : 'none',
                  transition: 'filter 0.15s ease-in-out',
                  // Prevent text selection of the mask itself when hidden so
                  // a casual select-and-copy doesn't pull the bullet string.
                  userSelect: hidden ? 'none' : 'auto',
                }}
                aria-label={
                  metric.sensitive && hidden
                    ? `${metric.title} hidden`
                    : undefined
                }
              >
                {displayedValue}
              </Typography>
              {metric.tooltip ? (
                <Tooltip title={metric.tooltip} arrow>
                  <Typography
                    variant="body1"
                    sx={{
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      lineHeight: 1.2,
                      fontSize: { xs: '0.95rem', sm: '1.02rem' },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {metric.title}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                    fontSize: { xs: '0.95rem', sm: '1.02rem' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {metric.title}
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.45,
                  color: sublineColor || '#cbd5e1',
                  fontWeight: 600,
                  fontSize: { xs: '0.72rem', sm: '0.78rem' },
                  lineHeight: 1.2,
                  minHeight: '1.2em',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  visibility: metric.subline ? 'visible' : 'hidden',
                  filter: hidden && metric.subline ? 'blur(4px)' : 'none',
                  transition: 'filter 0.15s ease-in-out',
                  userSelect: hidden && metric.subline ? 'none' : 'auto',
                }}
              >
                {displayedSubline || '\u00A0'}
              </Typography>
            </MetricCard>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default MetricsCards;
